import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Phone, Mail, MapPin, MessageCircle, Send, Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { CONTACT, SERVICES } from "@/data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contact() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    service: "",
    origin_country: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);

  const update = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill your name, email, and a short message.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Thanks — our desk will reply within 15 minutes (24/7).");
      setForm({
        name: "",
        email: "",
        phone: "",
        company: "",
        service: "",
        origin_country: "",
        message: "",
      });
    } catch (err) {
      console.error(err);
      toast.error("Could not send. Please WhatsApp us at +91 78003 20078.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="border-b border-[#E4E7EC] bg-[#F9FAFB]"
    >
      <div className="mx-auto max-w-7xl px-5 py-20 md:px-8 md:py-28">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-12 lg:gap-12">
          <div className="lg:col-span-5">
            <p className="overline mb-4">§ 11 — Get in touch</p>
            <h2 className="font-display text-4xl font-black leading-[0.95] tracking-tight text-[#101828] sm:text-5xl md:text-6xl">
              Ship into India<br />
              <span className="text-[#D92D20]">with confidence.</span>
            </h2>
            <p className="mt-6 max-w-md text-base leading-relaxed text-[#475467]">
              Reach us any time — our desk is staffed 24/7. Fastest response
              via WhatsApp. For quotes, send us the origin, HAWB/MAWB pattern,
              cargo type, and destination pincode.
            </p>

            <div className="mt-10 space-y-3">
              <a
                href={CONTACT.whatsappHref}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="contact-whatsapp"
                className="flex items-center gap-4 border-2 border-[#101828] bg-white p-5 hover:border-[#25D366]"
              >
                <span className="flex h-11 w-11 items-center justify-center bg-[#25D366]">
                  <MessageCircle className="h-5 w-5 text-white" strokeWidth={2.5} />
                </span>
                <div>
                  <div className="font-mono-accent text-[#475467]">WHATSAPP · FASTEST</div>
                  <div className="font-display text-lg font-bold text-[#101828]">
                    {CONTACT.phoneDisplay}
                  </div>
                </div>
              </a>
              <a
                href={CONTACT.telHref}
                data-testid="contact-phone"
                className="flex items-center gap-4 border-2 border-[#101828] bg-white p-5 hover:border-[#D92D20]"
              >
                <span className="flex h-11 w-11 items-center justify-center bg-[#101828]">
                  <Phone className="h-5 w-5 text-white" strokeWidth={2.5} />
                </span>
                <div>
                  <div className="font-mono-accent text-[#475467]">CALL DESK · 24/7</div>
                  <div className="font-display text-lg font-bold text-[#101828]">
                    {CONTACT.phoneDisplay}
                  </div>
                </div>
              </a>
              <a
                href={CONTACT.emailHref}
                data-testid="contact-email"
                className="flex items-center gap-4 border-2 border-[#101828] bg-white p-5 hover:border-[#FDB022]"
              >
                <span className="flex h-11 w-11 items-center justify-center bg-[#FDB022]">
                  <Mail className="h-5 w-5 text-[#101828]" strokeWidth={2.5} />
                </span>
                <div>
                  <div className="font-mono-accent text-[#475467]">EMAIL</div>
                  <div className="font-display text-lg font-bold text-[#101828]">
                    {CONTACT.email}
                  </div>
                </div>
              </a>
              <div
                className="flex items-start gap-4 border-2 border-[#101828] bg-white p-5"
                data-testid="contact-address"
              >
                <span className="flex h-11 w-11 flex-none items-center justify-center bg-[#101828]">
                  <MapPin className="h-5 w-5 text-white" strokeWidth={2.5} />
                </span>
                <div>
                  <div className="font-mono-accent text-[#475467]">OFFICE</div>
                  <div className="font-display text-base font-bold leading-tight text-[#101828]">
                    {CONTACT.address.line1}
                    <br />
                    {CONTACT.address.city}, {CONTACT.address.state}{" "}
                    {CONTACT.address.pincode}, {CONTACT.address.country}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <form
            onSubmit={submit}
            data-testid="contact-form"
            className="border-2 border-[#101828] bg-white p-6 md:p-8 lg:col-span-7"
          >
            <div className="mb-6 flex items-center justify-between border-b border-[#101828] pb-4">
              <div>
                <p className="font-mono-accent text-[#D92D20]">REQUEST A QUOTE</p>
                <h3 className="mt-1 font-display text-2xl font-bold tracking-tight text-[#101828]">
                  Tell us about your shipment
                </h3>
              </div>
            </div>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div>
                <label className="font-mono-accent mb-2 block text-[#475467]" htmlFor="name">
                  YOUR NAME *
                </label>
                <Input
                  id="name"
                  required
                  value={form.name}
                  onChange={update("name")}
                  data-testid="form-name"
                  className="rounded-none border-[#101828] focus-visible:ring-[#D92D20]"
                  placeholder="Full name"
                />
              </div>
              <div>
                <label className="font-mono-accent mb-2 block text-[#475467]" htmlFor="email">
                  EMAIL *
                </label>
                <Input
                  id="email"
                  type="email"
                  required
                  value={form.email}
                  onChange={update("email")}
                  data-testid="form-email"
                  className="rounded-none border-[#101828] focus-visible:ring-[#D92D20]"
                  placeholder="you@company.com"
                />
              </div>
              <div>
                <label className="font-mono-accent mb-2 block text-[#475467]" htmlFor="phone">
                  PHONE / WHATSAPP
                </label>
                <Input
                  id="phone"
                  value={form.phone}
                  onChange={update("phone")}
                  data-testid="form-phone"
                  className="rounded-none border-[#101828] focus-visible:ring-[#D92D20]"
                  placeholder="+1 555 000 0000"
                />
              </div>
              <div>
                <label className="font-mono-accent mb-2 block text-[#475467]" htmlFor="company">
                  COMPANY
                </label>
                <Input
                  id="company"
                  value={form.company}
                  onChange={update("company")}
                  data-testid="form-company"
                  className="rounded-none border-[#101828] focus-visible:ring-[#D92D20]"
                  placeholder="Forwarder / brand"
                />
              </div>
              <div>
                <label className="font-mono-accent mb-2 block text-[#475467]" htmlFor="service">
                  SERVICE NEEDED
                </label>
                <select
                  id="service"
                  value={form.service}
                  onChange={update("service")}
                  data-testid="form-service"
                  className="h-10 w-full rounded-none border border-[#101828] bg-white px-3 py-2 text-sm text-[#101828] focus:outline-none focus:ring-2 focus:ring-[#D92D20]"
                >
                  <option value="">Select service</option>
                  {SERVICES.map((s) => (
                    <option key={s.id} value={s.name}>
                      {s.name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="font-mono-accent mb-2 block text-[#475467]" htmlFor="origin_country">
                  ORIGIN COUNTRY
                </label>
                <Input
                  id="origin_country"
                  value={form.origin_country}
                  onChange={update("origin_country")}
                  data-testid="form-origin"
                  className="rounded-none border-[#101828] focus-visible:ring-[#D92D20]"
                  placeholder="e.g. Germany"
                />
              </div>
              <div className="md:col-span-2">
                <label className="font-mono-accent mb-2 block text-[#475467]" htmlFor="message">
                  SHIPMENT DETAILS *
                </label>
                <Textarea
                  id="message"
                  required
                  rows={5}
                  value={form.message}
                  onChange={update("message")}
                  data-testid="form-message"
                  className="rounded-none border-[#101828] focus-visible:ring-[#D92D20]"
                  placeholder="Origin airport, HAWB, weight, cargo type, destination pincode, urgency…"
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              data-testid="form-submit"
              className="mt-6 inline-flex w-full items-center justify-center gap-2 border-2 border-[#101828] bg-[#D92D20] px-6 py-4 font-semibold text-white shadow-hard hover:bg-[#B42318] disabled:opacity-70 md:w-auto"
            >
              {loading ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" /> Sending…
                </>
              ) : (
                <>
                  <Send className="h-4 w-4" strokeWidth={2.5} /> Request quote
                </>
              )}
            </button>
            <p className="mt-4 text-xs text-[#475467]">
              By submitting, you agree to be contacted about your shipment. We
              never share your details.
            </p>
          </form>
        </div>
      </div>
    </section>
  );
}
